import { Dispatcher } from "./api/dispatcher"
import { HTTP_STATUS } from "./constants"

import { appendLangToUrl } from "./redux/api/fetchBaseQueryExtended"
import { FEATURE } from "./redux/payment/paymentSlice"

const dispatcher = Dispatcher.getInstance()

/**
 * JOAN-10552: thin wrapper around `fetch`. Handles auth/payment side
 * effects globally so legacy callers in `src/api/fetch.ts` (postJSON,
 * putJSON, etc.) propagate consistent state changes on 401/402. No
 * automatic retries — callers see the first response (success or error)
 * and decide what to do.
 */
export async function fetchExtended(
  url: RequestInfo,
  settings: RequestInit,
): Promise<Response> {
  url = appendLangToUrl(url.toString())

  const response = await fetch(url, settings)

  // If 2xx code, continue
  if (response.ok) {
    return response
  }

  if (response.status === HTTP_STATUS.UNAUTHORIZED) {
    dispatcher.dispatch("clearAuth")

    return response
  }

  if (response.status === HTTP_STATUS.PAYMENT_REQUIRED) {
    const lockedFeature = detectLockedFeature(url)
    dispatcher.dispatch("setHasPaid", lockedFeature)
  }

  return response
}

// Parts of URLs for detecting which feature is locked
const DESK_BOOKING_LOCK_URLS = [
  /desk\/company\/[\w-]+\/reservation/,
  /v2\/me\/reservation/,
  /reservation\/[/\w-]+\//,
  /portal\/desks\/schedule\//,
  /portal\/people\/schedule\//,
  /portal\/desks\/reservations\//,
]

const ASSET_BOOKING_LOCK_URLS = [
  "/assets/schedule/",
  "/portal/assets/reservations",
]

const VISITOR_LOCK_URLS = ["portal/visitors/"]

const ROOM_BOOKING_LOCK_URLS = ["portal/rooms/"]

// When the API returns 402, we infer which feature to mark as locked from the request URL.
export const detectLockedFeature = (url: RequestInfo) => {
  const urlStr = url.toString()

  if (
    ASSET_BOOKING_LOCK_URLS.reduce(
      (isLocked, path) => urlStr.includes(path) || isLocked,
      false,
    )
  ) {
    return FEATURE.desk
  }

  if (
    DESK_BOOKING_LOCK_URLS.reduce(
      (isLocked, pathRegEx) => pathRegEx.test(urlStr) || isLocked,
      false,
    )
  ) {
    return FEATURE.desk
  }

  if (
    VISITOR_LOCK_URLS.reduce(
      (isLocked, path) => urlStr.includes(path) || isLocked,
      false,
    )
  ) {
    return FEATURE.visitor
  }

  if (
    ROOM_BOOKING_LOCK_URLS.reduce(
      (isLocked, path) => urlStr.includes(path) || isLocked,
      false,
    )
  ) {
    return FEATURE.room
  }

  return null
}
